import { useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import { Paperclip, Star } from "lucide-react";
import { Avatar } from "@dfws/ui";
import type { Density } from "@dfws/contracts";
import type { ListThread } from "./types";
import { listDate } from "./format";
import s from "./MessageList.module.css";

const ROW: Record<Density, number> = { compact: 32, cozy: 40, comfortable: 56 };
const TWO_LINE = 64;

export interface MessageListProps {
  threads: ListThread[];
  /** All conversations in the folder, including pages not loaded yet (for screen readers). */
  total?: number;
  density: Density;
  /** Two-line rows for a narrow pane or a phone, whatever the density. */
  twoLine: boolean;
  cursor: number;
  openId: string | null;
  checked: Set<string>;
  now: Date;
  onCursor: (index: number) => void;
  onOpen: (id: string) => void;
  onToggleCheck: (id: string, range: boolean) => void;
  onToggleStar: (id: string) => void;
  /** Called when the list nears its end, to load the next page. */
  onEndReached?: () => void;
  /** Pointer rests on a row: a cue to warm that conversation. */
  onRowHover?: (index: number) => void;
  /** Pointer pressed on a row: start loading before the click completes. */
  onRowPress?: (index: number) => void;
}

export function MessageList({ threads, total, density, twoLine, cursor, openId, checked, now, onCursor, onOpen, onToggleCheck, onToggleStar, onEndReached, onRowHover, onRowPress }: MessageListProps) {
  const scroller = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/incompatible-library
  const v = useVirtualizer({ count: threads.length, getScrollElement: () => scroller.current, estimateSize: () => (twoLine ? TWO_LINE : ROW[density]), overscan: 12 });

  useEffect(() => {
    v.measure();
  }, [density, twoLine, v]);

  useEffect(() => {
    if (cursor >= 0) v.scrollToIndex(cursor, { align: "auto" });
  }, [cursor, v]);

  const items = v.getVirtualItems();
  const last = items[items.length - 1]?.index ?? 0;
  useEffect(() => {
    if (onEndReached && last >= threads.length - 20) onEndReached();
  }, [last, threads.length, onEndReached]);

  return (
    <div ref={scroller} className={clsx(s.scroller, s[density], twoLine && s.twoLine)} role="grid" aria-label="Conversations" aria-rowcount={total ?? threads.length}>
      <div style={{ height: v.getTotalSize(), position: "relative" }}>
        {items.map((item) => {
          const t = threads[item.index];
          if (!t) return null;
          const isChecked = checked.has(t.id);
          return (
            <div
              key={t.id}
              role="row"
              aria-rowindex={item.index + 1}
              aria-selected={openId === t.id}
              data-cursor={item.index === cursor || undefined}
              className={clsx(s.row, t.unread && s.unread, openId === t.id && s.open, isChecked && s.checked)}
              style={{ height: item.size, transform: `translateY(${item.start}px)` }}
              onMouseEnter={onRowHover ? () => onRowHover(item.index) : undefined}
              onPointerDown={onRowPress ? () => onRowPress(item.index) : undefined}
              onClick={(e) => {
                onCursor(item.index);
                if (e.shiftKey || e.metaKey || e.ctrlKey) onToggleCheck(t.id, e.shiftKey);
                else onOpen(t.id);
              }}
            >
              <span role="gridcell" className={s.lead}>
                <input
                  type="checkbox"
                  className={s.check}
                  checked={isChecked}
                  aria-label={`Select conversation from ${t.from.name}`}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onToggleCheck(t.id, (e.nativeEvent as MouseEvent).shiftKey)}
                />
                {(density === "comfortable" || twoLine) && <Avatar name={t.from.name} email={t.from.email} size={32} className={s.avatar} />}
                <button
                  type="button"
                  className={clsx(s.star, t.starred && s.starred)}
                  aria-label={t.starred ? "Unstar" : "Star"}
                  aria-pressed={t.starred}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleStar(t.id);
                  }}
                >
                  <Star />
                </button>
              </span>
              <span role="gridcell" className={s.from} dir="auto" title={t.from.email}>
                {t.unread && <span className={s.dot} aria-label="Unread" />}
                <span className={s.fromName}>{t.from.name}</span>
                {t.count > 1 && <span className={clsx(s.count, "df-tabular")}>{t.count}</span>}
              </span>
              <span role="gridcell" className={s.body}>
                {t.labels.map((l) => (
                  <span key={l.name} className={s.label} style={{ ["--hue" as string]: `var(--label-${l.hue})` }}>
                    {l.name}
                  </span>
                ))}
                <span className={clsx(s.subject, !t.subject && s.noSubject)} dir="auto">
                  {t.subject || "(no subject)"}
                </span>
                <span className={s.snippet} dir="auto">
                  {t.snippet}
                </span>
              </span>
              <span role="gridcell" className={s.meta}>
                {t.hasAttachment && <Paperclip className={s.clip} aria-label="Has attachment" />}
                <time className="df-tabular" dateTime={t.date.toISOString()}>
                  {listDate(t.date, now)}
                </time>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
